import React, { useEffect, useState } from "react";
import { Plus, Sparkles } from "lucide-react";
import { AppButton, AppModal, Callout, Checkbox, FormField, Input, Textarea } from "../ui";
import { adminCreateSession, adminUpdateSession } from "../../utils/smartLearning";
import ScormPlayer from "../SelfPaced/ScormPlayer";
import ResourcePicker from "../LearningResources/ResourcePicker";
import ResourceEditor from "../LearningResources/ResourceEditor";
import ResourceList from "../LearningResources/ResourceList";
import ResourceWorkspace from "../LearningResources/ResourceWorkspace";
import { ADDABLE_KINDS, FULL_EDITOR_HREF, KIND_META } from "../LearningResources/resourceKinds";
import UnlockRulesFields, { DEFAULT_UNLOCK, cleanUnlockConditions } from "./UnlockRulesFields";

const FORM_ID = "smart-session-editor";

const blank = () => ({
  title: "",
  description: "",
  plannedDurationMinutes: 60,
  unlockMode: "afterClass",
  unlockConditions: { ...DEFAULT_UNLOCK },
  resources: [],
  allowTrainerAdditions: false,
});

/**
 * SessionEditorDialog — add or edit one hand-authored Smart Learning session
 * of a module: what it teaches, its resources, and what opens the next one.
 *
 * Saving a course session reaches every running batch that has not taught it
 * yet (the server copies it across).
 *
 * Props: show, onHide, courseId, moduleNumber, moduleTitle, codingEnabled,
 *        session (edit) | null (add), onSaved(session)
 */
const SessionEditorDialog = ({ show, onHide, courseId, moduleNumber, moduleTitle, codingEnabled, session, onSaved }) => {
  const isEdit = !!session?._id;
  const [form, setForm] = useState(blank);
  const [panel, setPanel] = useState(null); // { mode: 'pick', kind, index } | { mode: 'edit', index }
  const [scorm, setScorm] = useState(null);
  const [errors, setErrors] = useState({});
  const [failure, setFailure] = useState("");
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!show) return;
    setForm(session ? {
      title: session.title || "",
      description: session.description || "",
      plannedDurationMinutes: session.plannedDurationMinutes || 60,
      unlockMode: session.unlockMode || "afterClass",
      unlockConditions: { ...DEFAULT_UNLOCK, ...(session.unlockConditions || {}) },
      resources: Array.isArray(session.resources) ? session.resources : [],
      allowTrainerAdditions: !!session.allowTrainerAdditions,
    } : blank());
    setPanel(null);
    setErrors({});
    setFailure("");
    setSaving(false);
  }, [show, session]);

  const set = (patch) => setForm((f) => ({ ...f, ...patch }));
  const kinds = ADDABLE_KINDS.filter((k) => codingEnabled || k !== "codingChallenge");

  const placePicked = (picked, index) => {
    const row = {
      kind: picked.kind,
      refId: picked._id,
      title: picked.title,
      contentType: picked.contentType || "",
      durationMinutes: index >= 0 ? form.resources[index]?.durationMinutes || 0 : 0,
    };
    setForm((f) => {
      if (index >= 0) return { ...f, resources: f.resources.map((r, i) => (i === index ? { ...r, ...row } : r)) };
      if (f.resources.some((r) => r.kind === row.kind && String(r.refId) === String(row.refId))) return f;
      return { ...f, resources: [...f.resources, row] };
    });
  };

  const submit = async (e) => {
    e.preventDefault();
    const next = {};
    if (!form.title.trim()) next.title = "Give the session a title.";
    const minutes = Number(form.plannedDurationMinutes);
    if (!(minutes >= 5 && minutes <= 600)) next.plannedDurationMinutes = "Between 5 and 600 minutes.";
    setErrors(next);
    if (Object.values(next).some(Boolean)) return;

    setSaving(true);
    setFailure("");
    try {
      const payload = {
        courseId,
        moduleNumber,
        title: form.title.trim(),
        description: form.description.trim(),
        plannedDurationMinutes: minutes,
        unlockMode: form.unlockMode,
        unlockConditions: cleanUnlockConditions(form.unlockConditions),
        resources: form.resources,
        allowTrainerAdditions: form.allowTrainerAdditions,
      };
      const saved = isEdit ? await adminUpdateSession(session._id, payload) : await adminCreateSession(payload);
      onSaved?.(saved);
      onHide();
    } catch (err) {
      setFailure(err?.response?.data?.message || "The session was not saved. Try again.");
      setSaving(false);
    }
  };

  const main = (
    <form id={FORM_ID} onSubmit={submit} noValidate className="tw-flex tw-flex-col tw-gap-5">
      {failure && <Callout tone="danger" role="alert">{failure}</Callout>}
      <FormField label="Title" required error={errors.title}>
        <Input value={form.title} onChange={(e) => set({ title: e.target.value })} placeholder="e.g. Variables and data types" maxLength={200} autoFocus />
      </FormField>
      <FormField label="What students learn" hint="Shown to students on their learning path.">
        <Textarea rows={2} value={form.description} onChange={(e) => set({ description: e.target.value })} />
      </FormField>
      <FormField label="Length (minutes)" error={errors.plannedDurationMinutes} hint="Attendance is measured against this.">
        <Input type="number" min={5} max={600} fullWidth={false} className="tw-w-32" value={form.plannedDurationMinutes} onChange={(e) => set({ plannedDurationMinutes: e.target.value })} />
      </FormField>

      <section className="tw-flex tw-flex-col tw-gap-3">
        <h3 className="tw-m-0 tw-text-sm tw-font-semibold tw-text-content">Resources</h3>
        <ResourceList
          resources={form.resources}
          empty="No resources yet — add the documents, assignments or quizzes that come with this session."
          onEdit={(r, i) => (r.kind === "scorm" ? setScorm(r.refId) : setPanel({ mode: "edit", index: i }))}
          onReplace={(r, i) => setPanel({ mode: "pick", kind: r.kind === "scorm" ? "document" : r.kind, index: i })}
          onRemove={(_r, i) => setForm((f) => ({ ...f, resources: f.resources.filter((_, j) => j !== i) }))}
          onMinutes={(_r, i, v) => setForm((f) => ({ ...f, resources: f.resources.map((r, j) => (j === i ? { ...r, durationMinutes: v } : r)) }))}
        />
        <div className="tw-flex tw-flex-wrap tw-gap-2">
          {kinds.map((k) => {
            const { Icon, label } = KIND_META[k];
            return (
              <AppButton key={k} type="button" size="sm" variant="outline" onClick={() => setPanel({ mode: "pick", kind: k, index: -1 })}>
                <Plus size={13} aria-hidden="true" /><Icon size={13} aria-hidden="true" /> {label}
              </AppButton>
            );
          })}
        </div>
        <Checkbox
          checked={form.allowTrainerAdditions}
          onChange={(e) => set({ allowTrainerAdditions: e.target.checked })}
          label="Let the trainer add documents or assignments while teaching it"
        />
      </section>

      <section className="tw-flex tw-flex-col tw-gap-3 tw-rounded-md tw-bg-surface-sunken tw-p-4">
        <h3 className="tw-m-0 tw-text-sm tw-font-semibold tw-text-content">Unlocking</h3>
        <UnlockRulesFields
          value={{ unlockMode: form.unlockMode, unlockConditions: form.unlockConditions }}
          onChange={(v) => set({ unlockMode: v.unlockMode, unlockConditions: v.unlockConditions })}
        />
      </section>
    </form>
  );

  let aside = null;
  if (panel?.mode === "pick") {
    aside = (
      <ResourcePicker
        key={`${panel.kind}-${panel.index}`}
        kind={panel.kind}
        courseId={courseId}
        initialTab={panel.index >= 0 ? "pick" : "create"}
        moduleTitle={moduleTitle}
        sessionTitle={form.title}
        sessionDescription={form.description}
        onPick={(picked) => placePicked(picked, panel.index)}
        onClose={() => setPanel(null)}
      />
    );
  } else if (panel?.mode === "edit" && form.resources[panel.index]) {
    const r = form.resources[panel.index];
    aside = (
      <ResourceEditor
        key={`edit-${r.refId}`}
        resource={r}
        courseId={courseId}
        context={{ moduleTitle, sessionTitle: form.title, sessionDescription: form.description }}
        onSaved={(saved) => {
          if (!saved?._id) return;
          setForm((f) => ({
            ...f,
            resources: f.resources.map((x) => (String(x.refId) === String(saved._id) ? { ...x, title: saved.title || x.title } : x)),
          }));
        }}
        onClose={() => setPanel(null)}
        onOpenFull={(kind, refId) => window.open(FULL_EDITOR_HREF[kind]?.(refId), "_blank", "noopener")}
      />
    );
  }

  return (
    <>
      <AppModal
        show={show}
        onHide={saving ? undefined : onHide}
        closeOnOverlay={false}
        size="xl"
        title={isEdit ? "Edit session" : "Add a session"}
        icon={<Sparkles size={18} aria-hidden="true" />}
        footer={(
          <div className="tw-flex tw-w-full tw-flex-wrap tw-items-center tw-justify-between tw-gap-2">
            <span className="tw-text-xs tw-text-content-muted">Module {moduleNumber}{moduleTitle ? ` · ${moduleTitle}` : ""}</span>
            <span className="tw-flex tw-gap-2">
              <AppButton variant="outline" onClick={onHide} disabled={saving}>Cancel</AppButton>
              <AppButton type="submit" form={FORM_ID} disabled={saving}>
                {saving ? "Saving…" : isEdit ? "Save session" : "Add session"}
              </AppButton>
            </span>
          </div>
        )}
      >
        <ResourceWorkspace main={main} aside={aside} />
      </AppModal>
      <AppModal show={!!scorm} onHide={() => setScorm(null)} size="xl" title="SCORM preview">
        <div className="tw-h-[70vh]">{scorm && <ScormPlayer lessonId={scorm} />}</div>
      </AppModal>
    </>
  );
};

export default SessionEditorDialog;
